import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  ExternalLink,
  GripVertical,
  Star,
  Trash2,
  Upload,
  Plus,
  ImageOff,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  DndContext, closestCenter, SortableContext, arrayMove,
  verticalListSortingStrategy, rectSortingStrategy, SortableItem, useDndSensors,
} from "../components/Sortable";
import {
  apiGetProduct, apiUpdateProduct, apiMeta, apiUploadImage, apiDeleteAsset,
  apiGenerateVariants, eur, errMsg, cldThumb, GRADE_OPTIONS, GRADE_LABELS,
} from "../adminApi";
import { useEditorGuard, canLeave } from "../EditorGuard";
import { ReviewDialog } from "../components/ReviewDialog";
import { apiProductReviews } from "../adminApi";
import { Star as StarIcon } from "lucide-react";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

export default function ProductEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [meta, setMeta] = useState({ brands: [], categories: [], cloudinary_configured: false });
  const { dirty, setDirty } = useEditorGuard();
  const [saving, setSaving] = useState(false);
  const tab = searchParams.get("tab") || "info";

  // reset dello stato "modifiche non salvate" all'uscita dall'editor
  useEffect(() => {
    setDirty(false);
    return () => setDirty(false);
  }, [id, setDirty]);

  useEffect(() => {
    apiMeta().then(setMeta).catch(() => {});
  }, []);
  useEffect(() => {
    apiGetProduct(id).then(setProduct).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  const setField = useCallback((patch) => {
    setProduct((p) => ({ ...p, ...patch }));
    setDirty(true);
  }, []);

  const save = useCallback(async (silent = false) => {
    if (!product) return false;
    setSaving(true);
    try {
      const updated = await apiUpdateProduct(id, product);
      setProduct(updated);
      setDirty(false);
      if (!silent) toast.success("Salvato", { id: "save" });
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [product, id]);

  // Avviso su chiusura/reload della scheda se ci sono modifiche non salvate
  useEffect(() => {
    const beforeUnload = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);

  if (!product) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-12 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  const setTab = (v) => setSearchParams({ tab: v }, { replace: true });

  return (
    <div className="flex flex-col gap-5 pb-24">
      {/* Sticky save bar */}
      <div className="sticky top-14 z-20 -mx-4 flex flex-wrap items-center gap-3 border-b border-line bg-ink/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { if (canLeave(dirty)) { setDirty(false); navigate("/admin/prodotti"); } }} aria-label="Indietro">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-base font-semibold text-chalk">{product.name || "Senza nome"}</p>
          {dirty && <span className="text-xs text-lime" data-testid="product-editor-unsaved-indicator">Modifiche non salvate</span>}
        </div>
        <Select value={product.status} onValueChange={(v) => setField({ status: v })}>
          <SelectTrigger className="h-9 w-[140px]" data-testid="product-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="DRAFT">Bozza</SelectItem>
            <SelectItem value="PUBLISHED">Pubblicato</SelectItem>
          </SelectContent>
        </Select>
        <a href={`/prodotto/${product.slug}`} target="_blank" rel="noreferrer" data-testid="product-editor-view-on-site-button">
          <Button variant="secondary" size="sm"><ExternalLink className="h-4 w-4" /> <span className="hidden sm:inline">Vedi sul sito</span></Button>
        </a>
        <Button variant="lime" size="sm" onClick={() => save(false)} disabled={saving || !dirty} data-testid="product-editor-save-button">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva
        </Button>
      </div>

      {product.status === "PUBLISHED" && product.publish_missing?.length > 0 && (
        <div className="rounded-xl border border-carrot/40 bg-surface2 px-4 py-3 text-sm text-carrot">
          Per pubblicare manca: {product.publish_missing.join(", ")}.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="product-editor-tabs" className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="info">Info</TabsTrigger>
          <TabsTrigger value="media">Colori e foto</TabsTrigger>
          <TabsTrigger value="variants">Varianti e prezzi</TabsTrigger>
          <TabsTrigger value="reviews">Recensioni</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-5">
          <InfoTab product={product} setField={setField} meta={meta} />
        </TabsContent>
        <TabsContent value="media" className="mt-5">
          <MediaTab product={product} setProduct={setProduct} setDirty={setDirty} save={save} cloudinaryOk={meta.cloudinary_configured} />
        </TabsContent>
        <TabsContent value="variants" className="mt-5">
          <VariantsTab product={product} setProduct={setProduct} setField={setField} save={save} />
        </TabsContent>
        <TabsContent value="reviews" className="mt-5">
          <ReviewsTab product={product} />
        </TabsContent>
        <TabsContent value="seo" className="mt-5">
          <SeoTab product={product} setField={setField} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------------ INFO ------------------------------ */
function InfoTab({ product, setField, meta }) {
  const specs = product.specs || [];
  const updateSpec = (i, key, val) => {
    const next = specs.map((s, idx) => (idx === i ? { ...s, [key]: val } : s));
    setField({ specs: next });
  };
  const addSpec = () => setField({ specs: [...specs, { label: "", value: "" }] });
  const removeSpec = (i) => setField({ specs: specs.filter((_, idx) => idx !== i) });

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="flex flex-col gap-5 lg:col-span-2">
        <Card className="border-line bg-surface2">
          <CardHeader><CardTitle className="text-base">Informazioni</CardTitle></CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Field label="Nome">
              <Input value={product.name || ""} onChange={(e) => setField({ name: e.target.value })} data-testid="product-name-input" />
            </Field>
            <Field label="Slug" hint="Identificatore URL. Generato dal nome, modificabile.">
              <Input value={product.slug || ""} onChange={(e) => setField({ slug: e.target.value })} />
            </Field>
            <Field label="Descrizione breve">
              <Input value={product.short_description || ""} onChange={(e) => setField({ short_description: e.target.value })} />
            </Field>
            <Field label="Descrizione">
              <Textarea rows={6} value={product.description || ""} onChange={(e) => setField({ description: e.target.value })} />
            </Field>
          </CardContent>
        </Card>

        <Card className="border-line bg-surface2">
          <CardHeader><CardTitle className="text-base">Specifiche</CardTitle></CardHeader>
          <CardContent className="flex flex-col gap-2">
            {specs.length === 0 && <p className="text-sm text-steel">Nessuna specifica. Aggiungine una.</p>}
            {specs.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input placeholder="Nome" value={s.label} onChange={(e) => updateSpec(i, "label", e.target.value)} className="flex-1" />
                <Input placeholder="Valore" value={s.value} onChange={(e) => updateSpec(i, "value", e.target.value)} className="flex-1" />
                <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => removeSpec(i)} aria-label="Rimuovi"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="secondary" size="sm" className="mt-1 self-start" onClick={addSpec}><Plus className="h-4 w-4" /> Aggiungi specifica</Button>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-5">
        <Card className="border-line bg-surface2">
          <CardHeader><CardTitle className="text-base">Organizzazione</CardTitle></CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Field label="Marchio">
              <Select value={product.brand || ""} onValueChange={(v) => setField({ brand: v })}>
                <SelectTrigger data-testid="product-brand-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {meta.brands.map((b) => <SelectItem key={b.key} value={b.key}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Categoria principale">
              <Select value={product.category || ""} onValueChange={(v) => setField({ category: v })}>
                <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {meta.categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </CardContent>
        </Card>

        <Card className="border-line bg-surface2">
          <CardHeader><CardTitle className="text-base">VisibilitÃ </CardTitle></CardHeader>
          <CardContent className="flex flex-col gap-4">
            <ToggleRow label="In evidenza" checked={!!product.featured} onChange={(v) => setField({ featured: v })} testid="product-featured-switch" />
            <ToggleRow label="NovitÃ " checked={!!product.is_new} onChange={(v) => setField({ is_new: v })} testid="product-isnew-switch" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ------------------------------ MEDIA ------------------------------ */
function MediaTab({ product, setProduct, setDirty, save, cloudinaryOk }) {
  const sensors = useDndSensors();
  const colors = product.colors || [];
  const photos = product.photos || [];
  const [newColorName, setNewColorName] = useState("");
  const [newColorHex, setNewColorHex] = useState("#000000");
  const [activeScope, setActiveScope] = useState("common"); // color id o 'common'
  const [uploads, setUploads] = useState([]); // {name, pct, error}

  const persist = async (nextProduct) => {
    setProduct(nextProduct);
    setDirty(true);
    // salvataggio immediato per mantenere coerenza media/Cloudinary
    try {
      const updated = await apiUpdateProduct(product.id, nextProduct);
      setProduct(updated);
      setDirty(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const addColor = async () => {
    if (!newColorName.trim()) return;
    const next = { ...product, colors: [...colors, { id: uid(), name: newColorName.trim(), hex: newColorHex }] };
    setNewColorName("");
    await persist(next);
    toast.success("Colore aggiunto");
  };
  const removeColor = async (colorId) => {
    const next = {
      ...product,
      colors: colors.filter((c) => c.id !== colorId),
      photos: photos.filter((p) => p.color_id !== colorId),
      variants: (product.variants || []).map((v) => (v.color_id === colorId ? { ...v, color_id: null } : v)),
    };
    if (activeScope === colorId) setActiveScope("common");
    await persist(next);
    toast.success("Colore rimosso");
  };
  const onColorDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = colors.findIndex((c) => c.id === active.id);
    const newIndex = colors.findIndex((c) => c.id === over.id);
    await persist({ ...product, colors: arrayMove(colors, oldIndex, newIndex) });
  };

  const scopePhotos = photos
    .filter((p) => (activeScope === "common" ? !p.color_id : p.color_id === activeScope))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const handleFiles = async (fileList) => {
    if (!cloudinaryOk) {
      toast.error("Cloudinary non configurato. Aggiungi le chiavi nel server.");
      return;
    }
    const files = Array.from(fileList);
    const added = [];
    for (const file of files) {
      const key = uid();
      setUploads((u) => [...u, { key, name: file.name, pct: 0, error: false }]);
      try {
        const res = await apiUploadImage(file, "rinova/products", (evt) => {
          const pct = evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0;
          setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct } : x)));
        });
        added.push({
          id: uid(), public_id: res.public_id, url: res.url, alt: "",
          color_id: activeScope === "common" ? null : activeScope,
          is_primary: false, sort_order: 999,
        });
        setUploads((u) => u.filter((x) => x.key !== key));
      } catch (e) {
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: true } : x)));
        toast.error(`Errore caricamento ${file.name}`);
      }
    }
    if (added.length) {
      // se non c'Ã¨ principale nello scope, imposta la prima
      const scopeHasPrimary = scopePhotos.some((p) => p.is_primary);
      if (!scopeHasPrimary) added[0].is_primary = true;
      await persist({ ...product, photos: [...photos, ...added] });
      toast.success(`${added.length} foto caricate`);
    }
  };

  const deletePhoto = async (photo) => {
    try {
      if (photo.public_id) await apiDeleteAsset(photo.public_id);
    } catch (e) {
      toast.error(errMsg(e));
      return;
    }
    await persist({ ...product, photos: photos.filter((p) => p.id !== photo.id) });
    toast.success("Foto eliminata");
  };
  const setPrimary = async (photo) => {
    const next = photos.map((p) => {
      const sameScope = (activeScope === "common" ? !p.color_id : p.color_id === activeScope);
      if (!sameScope) return p;
      return { ...p, is_primary: p.id === photo.id };
    });
    await persist({ ...product, photos: next });
  };
  const setAlt = (photoId, alt) => {
    setProduct({ ...product, photos: photos.map((p) => (p.id === photoId ? { ...p, alt } : p)) });
    setDirty(true);
  };
  const onPhotoDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = scopePhotos.map((p) => p.id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    const reordered = arrayMove(scopePhotos, oldIndex, newIndex).map((p, i) => ({ ...p, sort_order: i }));
    const others = photos.filter((p) => !ids.includes(p.id));
    await persist({ ...product, photos: [...others, ...reordered] });
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      {/* Colori */}
      <Card className="border-line bg-surface2 lg:col-span-1">
        <CardHeader><CardTitle className="text-base">Colori</CardTitle></CardHeader>
        <CardContent className="flex flex-col gap-3">
          {!cloudinaryOk && (
            <p className="rounded-lg border border-carrot/40 bg-surface3 px-3 py-2 text-xs text-carrot">
              Cloudinary non configurato: l'upload foto Ã¨ disattivato finchÃ© non aggiungi le chiavi nel server.
            </p>
          )}
          <button
            className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors ${activeScope === "common" ? "border-lime/50 bg-surface3 text-chalk" : "border-line text-steel hover:bg-surface3"}`}
            onClick={() => setActiveScope("common")}
          >
            Foto comuni a tutti i colori
            <span className="text-xs text-tertiary">{photos.filter((p) => !p.color_id).length}</span>
          </button>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onColorDragEnd}>
            <SortableContext items={colors.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              <div className="flex flex-col gap-2" data-testid="product-colors-list">
                {colors.map((c, idx) => (
                  <SortableItem key={c.id} id={c.id}>
                    {({ setNodeRef, style, attributes, listeners }) => (
                      <div ref={setNodeRef} style={style} {...attributes}
                        className={`flex items-center gap-2 rounded-lg border px-2 py-2 ${activeScope === c.id ? "border-lime/50 bg-surface3" : "border-line"}`}>
                        <button className="cursor-grab text-tertiary" {...listeners} aria-label="Trascina"><GripVertical className="h-4 w-4" /></button>
                        <button className="flex flex-1 items-center gap-2 text-left" onClick={() => setActiveScope(c.id)}>
                          <span className="h-5 w-5 rounded-full border border-stroke" style={{ backgroundColor: c.hex }} />
                          <span className="text-sm text-chalk">{c.name}</span>
                          {idx === 0 && <span className="ml-1 text-[10px] text-lime">default</span>}
                        </button>
                        <span className="text-xs text-tertiary">{photos.filter((p) => p.color_id === c.id).length}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeColor(c.id)} aria-label="Rimuovi colore"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    )}
                  </SortableItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <div className="mt-2 flex items-center gap-2 border-t border-line pt-3">
            <input type="color" value={newColorHex} onChange={(e) => setNewColorHex(e.target.value)} className="h-9 w-9 cursor-pointer rounded-md border border-line bg-transparent" aria-label="Colore" data-testid="new-color-hex" />
            <Input placeholder="Nome colore" value={newColorName} onChange={(e) => setNewColorName(e.target.value)} className="flex-1" data-testid="new-color-name" />
            <Button variant="secondary" size="sm" onClick={addColor} data-testid="add-color-button"><Plus className="h-4 w-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Foto dello scope */}
      <Card className="border-line bg-surface2 lg:col-span-2">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">
            Foto â {activeScope === "common" ? "comuni" : (colors.find((c) => c.id === activeScope)?.name || "colore")}
          </CardTitle>
          <label>
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="cloudinary-upload-input" disabled={!cloudinaryOk} />
            <Button asChild variant="lime" size="sm" disabled={!cloudinaryOk} data-testid="product-media-upload-button">
              <span className="cursor-pointer"><Upload className="h-4 w-4" /> Carica foto</span>
            </Button>
          </label>
        </CardHeader>
        <CardContent>
          {uploads.length > 0 && (
            <div className="mb-3 flex flex-col gap-2">
              {uploads.map((u) => (
                <div key={u.key} data-testid="product-media-upload-progress">
                  <div className="flex justify-between text-xs text-steel"><span className="truncate">{u.name}</span><span>{u.error ? "Errore" : `${u.pct}%`}</span></div>
                  <Progress value={u.pct} className="h-1.5" />
                </div>
              ))}
            </div>
          )}

          {scopePhotos.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center rounded-lg border border-dashed border-stroke text-steel">
              <ImageOff className="mb-2 h-6 w-6 text-tertiary" />
              <p className="text-sm">Nessuna foto in questo riquadro.</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onPhotoDragEnd}>
              <SortableContext items={scopePhotos.map((p) => p.id)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="product-media-gallery">
                  {scopePhotos.map((photo) => (
                    <SortableItem key={photo.id} id={photo.id}>
                      {({ setNodeRef, style, attributes, listeners }) => (
                        <div ref={setNodeRef} style={style} {...attributes} className="group relative overflow-hidden rounded-lg border border-line bg-surface3">
                          <button className="absolute left-1 top-1 z-10 cursor-grab rounded bg-ink/60 p-1 text-chalk" {...listeners} aria-label="Trascina"><GripVertical className="h-3.5 w-3.5" /></button>
                          {photo.is_primary && <span className="absolute right-1 top-1 z-10 rounded bg-lime px-1.5 py-0.5 text-[10px] font-semibold text-ink">Principale</span>}
                          <img src={cldThumb(photo.url, "c_pad,w_400,h_400,b_transparent,f_auto,q_auto")} alt={photo.alt || ""} className="aspect-square w-full object-cover" />
                          <div className="flex flex-col gap-1 p-2">
                            <Input value={photo.alt || ""} onChange={(e) => setAlt(photo.id, e.target.value)} placeholder="Testo alternativo" className="h-7 text-xs" />
                            <div className="flex items-center justify-between">
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setPrimary(photo)} disabled={photo.is_primary}><Star className="h-3 w-3" /> Principale</Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deletePhoto(photo)} aria-label="Elimina foto"><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {/* Anteprima cliente: la galleria come la vedrà sul sito */}
          {activeScope !== "common" && (
            <div className="mt-5 border-t border-line pt-4">
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-steel">
                Anteprima cliente — {colors.find((c) => c.id === activeScope)?.name}
              </p>
              <CustomerPreview
                photos={[
                  ...photos.filter((p) => p.color_id === activeScope),
                  ...photos.filter((p) => !p.color_id),
                ].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0) || (a.sort_order ?? 0) - (b.sort_order ?? 0))}
                name={product.name}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ------------------------------ VARIANTS ------------------------------ */
function VariantsTab({ product, setProduct, setField, save }) {
  const colors = product.colors || [];
  const variants = product.variants || [];
  const [selRows, setSelRows] = useState([]);
  const [genColors, setGenColors] = useState(["__none__"]);
  const [genMemories, setGenMemories] = useState("");
  const [genGrades, setGenGrades] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [filterColor, setFilterColor] = useState("all");
  const [filterGrade, setFilterGrade] = useState("all");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPrice, setBulkPrice] = useState("");
  const [bulkQty, setBulkQty] = useState("");

  const colorName = (cid) => (cid ? (colors.find((c) => c.id === cid)?.name || "â") : "Senza colore");

  const updateVariant = (vid, patch) => {
    setProduct({ ...product, variants: variants.map((v) => (v.id === vid ? { ...v, ...patch } : v)) });
    setField({}); // marca dirty
  };
  const removeVariant = (vid) => setField({ variants: variants.filter((v) => v.id !== vid) });

  const toggleColorSel = (val) =>
    setGenColors((s) => (s.includes(val) ? s.filter((x) => x !== val) : [...s, val]));
  const toggleGradeSel = (val) =>
    setGenGrades((s) => (s.includes(val) ? s.filter((x) => x !== val) : [...s, val]));

  const generate = async () => {
    if (genGrades.length === 0) { toast.error("Seleziona almeno un grado."); return; }
    setGenerating(true);
    try {
      const memories = genMemories.split(",").map((m) => m.trim()).filter(Boolean);
      const payload = {
        colors: genColors.map((c) => (c === "__none__" ? null : c)),
        memories,
        grades: genGrades,
      };
      const res = await apiGenerateVariants(product.id, payload);
      setProduct({ ...product, variants: res.variants });
      toast.success(res.added > 0 ? `${res.added} varianti generate` : "Nessuna nuova variante (giÃ  presenti)");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setGenerating(false);
    }
  };

  const filtered = variants.filter((v) =>
    (filterColor === "all" || (v.color_id || "__none__") === filterColor) &&
    (filterGrade === "all" || v.grade === filterGrade)
  );

  // Avviso: combinazioni colore + memoria che esistono ma non hanno nessun grado attivo.
  const comboWarnings = useMemo(() => {
    const groups = {};
    for (const v of variants) {
      const key = `${v.color_id || ""}|${v.memory || ""}`;
      if (!groups[key]) groups[key] = { color_id: v.color_id || null, memory: v.memory || null, total: 0, active: 0 };
      groups[key].total += 1;
      if (v.active) groups[key].active += 1;
    }
    return Object.values(groups).filter((g) => g.total > 0 && g.active === 0);
  }, [variants]);

  const applyBulk = () => {
    const next = variants.map((v) => {
      if (!selRows.includes(v.id)) return v;
      const patch = {};
      if (bulkPrice !== "") patch.price = Number(bulkPrice);
      if (bulkQty !== "") patch.quantity = Number(bulkQty);
      return { ...v, ...patch };
    });
    setField({ variants: next });
    setBulkOpen(false); setBulkPrice(""); setBulkQty("");
    toast.success("Modifiche in blocco applicate. Ricordati di salvare.");
  };
  const bulkToggleActive = (active) => {
    setField({ variants: variants.map((v) => (selRows.includes(v.id) ? { ...v, active } : v)) });
  };
  const bulkDelete = () => {
    setField({ variants: variants.filter((v) => !selRows.includes(v.id)) });
    setSelRows([]);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Generatore */}
      <Card className="border-line bg-surface2" data-testid="variants-generator-card">
        <CardHeader><CardTitle className="text-base">Genera varianti</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div>
            <Label className="mb-2 block">Colori</Label>
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 text-sm text-chalk">
                <Checkbox checked={genColors.includes("__none__")} onCheckedChange={() => toggleColorSel("__none__")} /> Senza colore
              </label>
              {colors.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm text-chalk">
                  <Checkbox checked={genColors.includes(c.id)} onCheckedChange={() => toggleColorSel(c.id)} />
                  <span className="h-3.5 w-3.5 rounded-full border border-stroke" style={{ backgroundColor: c.hex }} /> {c.name}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="gen-mem" className="mb-2 block">Memorie</Label>
            <Input id="gen-mem" placeholder="es. 64, 128, 256 (vuoto = nessuna)" value={genMemories} onChange={(e) => setGenMemories(e.target.value)} />
            <p className="mt-1 text-xs text-tertiary">Separale con la virgola.</p>
          </div>
          <div>
            <Label className="mb-2 block">Gradi</Label>
            <div className="flex flex-col gap-1.5">
              {GRADE_OPTIONS.map((g) => (
                <label key={g.value} className="flex items-center gap-2 text-sm text-chalk">
                  <Checkbox checked={genGrades.includes(g.value)} onCheckedChange={() => toggleGradeSel(g.value)} /> {g.label}
                </label>
              ))}
            </div>
          </div>
          <div className="md:col-span-3">
            <Button variant="lime" size="sm" onClick={generate} disabled={generating} data-testid="variants-generate-button">
              {generating && <Loader2 className="h-4 w-4 animate-spin" />} Genera combinazioni mancanti
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Avviso combinazioni senza grado attivo */}
      {comboWarnings.length > 0 && (
        <div
          className="flex items-start gap-3 rounded-2xl border border-carrot/40 bg-carrot/[0.08] px-4 py-3"
          data-testid="variants-no-active-grade-warning"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-carrot" />
          <div className="text-sm text-chalk">
            <p className="font-medium">Alcune combinazioni non hanno nessun grado attivo</p>
            <p className="mt-1 text-steel">
              Il cliente non potrà selezionarle. Attiva almeno un grado per:{" "}
              {comboWarnings
                .map((g) => `${colorName(g.color_id)}${g.memory ? ` · ${g.memory}GB` : ""}`)
                .join(", ")}
              .
            </p>
          </div>
        </div>
      )}

      {/* Tabella varianti */}
      <Card className="border-line bg-surface2">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-base">Varianti ({variants.length})</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={filterColor} onValueChange={setFilterColor}>
              <SelectTrigger className="h-8 w-[130px]"><SelectValue placeholder="Colore" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i colori</SelectItem>
                <SelectItem value="__none__">Senza colore</SelectItem>
                {colors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterGrade} onValueChange={setFilterGrade}>
              <SelectTrigger className="h-8 w-[130px]"><SelectValue placeholder="Grado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i gradi</SelectItem>
                {GRADE_OPTIONS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {selRows.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" size="sm" data-testid="variants-bulk-edit-menu">Modifica in blocco ({selRows.length})</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Righe selezionate</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setBulkOpen(true)}>Imposta prezzo / quantitÃ </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => bulkToggleActive(true)}>Attiva</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => bulkToggleActive(false)}>Disattiva</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive" onClick={bulkDelete}>Elimina</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          {variants.length === 0 ? (
            <p className="p-6 text-sm text-steel">Nessuna variante. Usa il generatore qui sopra.</p>
          ) : (
            <Table data-testid="variants-inline-table">
              <TableHeader>
                <TableRow className="border-line hover:bg-transparent">
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Variante</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Prezzo</TableHead>
                  <TableHead>Prezzo nuovo</TableHead>
                  <TableHead>Prezzo di listino del nuovo (€)</TableHead>
                  <TableHead>Offerta</TableHead>
                  <TableHead>Q.tÃ </TableHead>
                  <TableHead className="text-amber-500/90">Costo acq. (€)</TableHead>
                  <TableHead className="text-amber-500/90">Costo rigen. (€)</TableHead>
                  <TableHead className="text-amber-500/90">Fornitore</TableHead>
                  <TableHead>Attiva</TableHead>
                  <TableHead className="w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((v) => (
                  <TableRow key={v.id} className="border-line">
                    <TableCell><Checkbox checked={selRows.includes(v.id)} onCheckedChange={() => setSelRows((s) => s.includes(v.id) ? s.filter((x) => x !== v.id) : [...s, v.id])} aria-label="Seleziona" /></TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      <span className="text-chalk">{colorName(v.color_id)}</span>
                      {v.memory && <span className="text-steel"> Â· {v.memory}GB</span>}
                      <span className="text-steel"> Â· {GRADE_LABELS[v.grade] || v.grade}</span>
                    </TableCell>
                    <TableCell><Input value={v.sku || ""} onChange={(e) => updateVariant(v.id, { sku: e.target.value })} className="h-8 w-32 text-xs" /></TableCell>
                    <TableCell><Input type="number" value={v.price ?? ""} onChange={(e) => updateVariant(v.id, { price: e.target.value === "" ? 0 : Number(e.target.value) })} className="h-8 w-24" /></TableCell>
                    <TableCell><Input type="number" value={v.compare_at_price ?? ""} onChange={(e) => updateVariant(v.id, { compare_at_price: e.target.value === "" ? null : Number(e.target.value) })} className="h-8 w-24" /></TableCell>
                    <TableCell><Input type="number" placeholder="vuoto" value={v.new_list_price_cents != null ? v.new_list_price_cents / 100 : ""} onChange={(e) => updateVariant(v.id, { new_list_price_cents: e.target.value === "" ? null : Math.round(Number(e.target.value) * 100) })} className="h-8 w-28" data-testid="variant-new-list-price" /></TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Input type="number" placeholder="Prezzo offerta" value={v.sale_price ?? ""} onChange={(e) => updateVariant(v.id, { sale_price: e.target.value === "" ? null : Number(e.target.value) })} className="h-8 w-28" data-testid="variant-sale-price" />
                        <Input type="datetime-local" value={v.sale_start ? String(v.sale_start).slice(0, 16) : ""} onChange={(e) => updateVariant(v.id, { sale_start: e.target.value ? new Date(e.target.value).toISOString() : null })} className="h-7 w-40 text-xs" />
                        <Input type="datetime-local" value={v.sale_end ? String(v.sale_end).slice(0, 16) : ""} onChange={(e) => updateVariant(v.id, { sale_end: e.target.value ? new Date(e.target.value).toISOString() : null })} className="h-7 w-40 text-xs" />
                      </div>
                    </TableCell>
                    <TableCell><Input type="number" value={v.quantity ?? 0} onChange={(e) => updateVariant(v.id, { quantity: e.target.value === "" ? 0 : Number(e.target.value) })} className="h-8 w-20" /></TableCell>
                    <TableCell><Input type="number" placeholder="vuoto" value={v.costo_acquisto_cents != null ? v.costo_acquisto_cents / 100 : ""} onChange={(e) => updateVariant(v.id, { costo_acquisto_cents: e.target.value === "" ? null : Math.round(Number(e.target.value) * 100) })} className="h-8 w-24" data-testid="variant-costo-acquisto" /></TableCell>
                    <TableCell><Input type="number" placeholder="vuoto" value={v.costo_rigenerazione_cents != null ? v.costo_rigenerazione_cents / 100 : ""} onChange={(e) => updateVariant(v.id, { costo_rigenerazione_cents: e.target.value === "" ? null : Math.round(Number(e.target.value) * 100) })} className="h-8 w-24" data-testid="variant-costo-rigenerazione" /></TableCell>
                    <TableCell><Input value={v.fornitore || ""} onChange={(e) => updateVariant(v.id, { fornitore: e.target.value })} className="h-8 w-32 text-xs" placeholder="Fornitore" data-testid="variant-fornitore" /></TableCell>
                    <TableCell><Switch checked={!!v.active} onCheckedChange={(c) => updateVariant(v.id, { active: c })} /></TableCell>
                    <TableCell><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeVariant(v.id)} aria-label="Elimina"><Trash2 className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modifica in blocco</AlertDialogTitle>
            <AlertDialogDescription>Applica a {selRows.length} righe. Lascia vuoto per non modificare.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-3">
            <Field label="Prezzo"><Input type="number" value={bulkPrice} onChange={(e) => setBulkPrice(e.target.value)} /></Field>
            <Field label="QuantitÃ "><Input type="number" value={bulkQty} onChange={(e) => setBulkQty(e.target.value)} /></Field>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-lime text-ink hover:bg-lime-bright" onClick={applyBulk}>Applica</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ------------------------------ SEO ------------------------------ */
function SeoTab({ product, setField }) {
  const seo = product.seo || { title: "", description: "" };
  const title = seo.title || product.name || "";
  const desc = seo.description || product.short_description || "";
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card className="border-line bg-surface2">
        <CardHeader><CardTitle className="text-base">SEO</CardTitle></CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Field label="Titolo SEO" hint={`${(seo.title || "").length}/60 caratteri`}>
            <Input value={seo.title || ""} onChange={(e) => setField({ seo: { ...seo, title: e.target.value } })} data-testid="seo-title-input" />
          </Field>
          <Field label="Meta description" hint={`${(seo.description || "").length}/160 caratteri`}>
            <Textarea rows={4} value={seo.description || ""} onChange={(e) => setField({ seo: { ...seo, description: e.target.value } })} data-testid="seo-description-textarea" />
          </Field>
          <Field label="Slug"><Input value={product.slug || ""} onChange={(e) => setField({ slug: e.target.value })} /></Field>
        </CardContent>
      </Card>
      <Card className="border-line bg-surface2">
        <CardHeader><CardTitle className="text-base">Anteprima Google</CardTitle></CardHeader>
        <CardContent>
          <div className="rounded-lg border border-line bg-surface3 p-4" data-testid="seo-google-preview">
            <p className="text-xs text-steel">rinova.it âº prodotto âº {product.slug}</p>
            <p className="mt-1 truncate text-lg text-[#8ab4f8]">{title}</p>
            <p className="mt-1 line-clamp-2 text-sm text-steel">{desc}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ------------------------------ REVIEWS ------------------------------ */
function ReviewsTab({ product }) {
  const [reviews, setReviews] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => apiProductReviews(product.id).then(setReviews).catch(() => setReviews([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [product.id]);
  return (
    <Card className="border-line bg-surface2">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Recensioni del prodotto</CardTitle>
        <Button variant="lime" size="sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="product-add-review-button"><Plus className="h-4 w-4" /> Aggiungi recensione</Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {!reviews ? <Skeleton className="h-16 rounded-lg" /> : reviews.length === 0 ? (
          <p className="py-4 text-sm text-steel">Ancora nessuna recensione per questo prodotto.</p>
        ) : reviews.map((r) => (
          <button key={r.id} onClick={() => { setEditing(r); setOpen(true); }} className="flex items-center gap-3 rounded-lg border border-line bg-surface3 px-3 py-2 text-left">
            <span className="inline-flex">{[1, 2, 3, 4, 5].map((i) => <StarIcon key={i} className={`h-3.5 w-3.5 ${i <= r.stars ? "fill-lime text-lime" : "text-tertiary"}`} />)}</span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-chalk">{r.title || r.display_name}</span>
              <span className="text-xs text-steel">{r.display_name} · {(r.photos || []).length} foto · {r.status === "PUBLISHED" ? "Pubblicata" : "Nascosta"}</span>
            </span>
          </button>
        ))}
      </CardContent>
      <ReviewDialog open={open} onOpenChange={setOpen} review={editing} presetProduct={product} onSaved={load} />
    </Card>
  );
}

/* ------------------------------ shared ------------------------------ */
function Field({ label, hint, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-tertiary">{hint}</p>}
    </div>
  );
}
function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-chalk">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

// Anteprima galleria come la vedrà il cliente sul sito (immagine grande + miniature).
function CustomerPreview({ photos = [], name = "Prodotto" }) {
  const [active, setActive] = useState(0);
  const sig = photos.map((p) => p.id).join("|");
  useEffect(() => { setActive(0); /* eslint-disable-next-line */ }, [sig]);
  const current = photos.length ? photos[Math.min(active, photos.length - 1)] : null;
  return (
    <div data-testid="product-media-customer-preview" className="max-w-sm">
      <div className="relative aspect-square w-full overflow-hidden rounded-2xl border border-line bg-[#111113]">
        {current ? (
          <img src={cldThumb(current.url, "e_trim/c_pad,w_800,h_800,b_transparent,f_auto,q_auto:best")} alt={current.alt || name} className="h-full w-full object-contain p-[10%]" />
        ) : (
          <div className="flex h-full w-full items-center justify-center px-4 text-center text-sm text-steel">
            <span>{name}</span>
          </div>
        )}
      </div>
      {photos.length > 1 && (
        <div className="mt-3 grid grid-cols-5 gap-2">
          {photos.slice(0, 10).map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setActive(i)}
              className={`overflow-hidden rounded-lg border bg-[#111113] transition-colors ${active === i ? "border-chalk" : "border-line hover:border-steel"}`}
            >
              <img src={cldThumb(p.url, "e_trim/c_pad,w_200,h_200,b_transparent,f_auto,q_auto:best")} alt={p.alt || ""} className="aspect-square w-full object-contain p-[12%]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
